import { useTranslation } from 'react-i18next';

import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  ErrorState,
  Skeleton,
} from '@/components/ui';
import { formatDate, formatNumber } from '@/lib/utils';

import type { BusinessSubscription } from './admin-schemas';
import { useBillingSummary } from './billing-api';
import { BillingStats, PlanDistribution } from './billing-overview';
import { money, planLabel, subscriptionTone } from './labels';

/**
 * דוח המנויים: שורה לכל עסק (תוכנית, מחזור, מצב, מושבים, MRR, תאריכים, קופון,
 * חינם) והתפלגות התוכניות. המצב הנוכחי — לא תלוי בטווח של הדוחות.
 */
export function BillingReport() {
  const { t } = useTranslation('admin');
  const q = useBillingSummary();
  const d = q.data;

  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!d) return <Skeleton className="h-48 w-full" />;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('billing.title')}</CardTitle>
            <CardDescription>{t('billing.hint')}</CardDescription>
          </CardHeader>
          <CardContent>
            <BillingStats summary={d} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.planDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <PlanDistribution plans={d.plans} />
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>{t('billing.table.title')}</CardTitle>
          <CardDescription>{t('billing.table.hint')}</CardDescription>
        </CardHeader>
        {d.rows.length === 0 ? (
          <CardContent>
            <p className="py-4 text-center text-xs text-fg-muted">{t('billing.table.empty')}</p>
          </CardContent>
        ) : (
          <DataTable<BusinessSubscription>
            caption={t('billing.table.title')}
            minWidth="64rem"
            rows={d.rows}
            rowKey={(r) => r.tenantId}
            columns={[
              {
                key: 'business',
                header: t('billing.table.columns.business'),
                cell: (r) => (
                  <span dir="auto" className="font-medium text-fg">
                    {r.tenantName}
                  </span>
                ),
              },
              {
                key: 'plan',
                header: t('billing.table.columns.plan'),
                cell: (r) => (
                  <span>
                    {planLabel(r.plan)}
                    {r.pendingPlan ? (
                      <span className="block text-2xs text-fg-subtle">
                        {t('billing.pendingPlan', { plan: planLabel(r.pendingPlan) })}
                      </span>
                    ) : null}
                  </span>
                ),
              },
              {
                key: 'interval',
                header: t('billing.table.columns.interval'),
                cell: (r) => t(`billing.interval.${r.interval}`),
              },
              {
                key: 'status',
                header: t('billing.table.columns.status'),
                cell: (r) => <SubscriptionStatusBadge sub={r} />,
              },
              {
                key: 'seats',
                header: t('billing.table.columns.seats'),
                numeric: true,
                cell: (r) => <SeatsValue sub={r} />,
              },
              {
                key: 'mrr',
                header: t('billing.table.columns.mrr'),
                numeric: true,
                cell: (r) => <bdi>{money(r.mrrMinor)}</bdi>,
              },
              {
                key: 'dates',
                header: t('billing.table.columns.dates'),
                cell: (r) => <SubscriptionDate sub={r} />,
              },
              {
                key: 'coupon',
                header: t('billing.table.columns.coupon'),
                cell: (r) => <CouponValue sub={r} />,
              },
              {
                key: 'complimentary',
                header: t('billing.table.columns.complimentary'),
                cell: (r) => (r.complimentary ? t('billing.yes') : '—'),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

export function SubscriptionStatusBadge({ sub }: { sub: BusinessSubscription }) {
  const { t } = useTranslation('admin');
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge tone={subscriptionTone(sub.status, sub.complimentary)}>
        {sub.complimentary && sub.status === 'ACTIVE'
          ? t('billing.counts.complimentary')
          : t(`billing.status.${sub.status}`)}
      </Badge>
      {sub.cancelAtPeriodEnd ? (
        <Badge tone="warning">{t('billing.cancelAtPeriodEnd')}</Badge>
      ) : null}
    </span>
  );
}

export function SeatsValue({ sub }: { sub: BusinessSubscription }) {
  const { t } = useTranslation('admin');
  return (
    <bdi className={sub.seatsUsed > sub.seatLimit ? 'text-danger' : undefined}>
      {t('billing.seatsValue', {
        used: formatNumber(sub.seatsUsed),
        limit: formatNumber(sub.seatLimit),
      })}
    </bdi>
  );
}

/** ניסיון → סוף הניסיון; אחרת — חידוש / סיום התקופה; חינם בלי תאריך — ללא הגבלה. */
export function SubscriptionDate({ sub }: { sub: BusinessSubscription }) {
  const { t } = useTranslation('admin');
  if (sub.status === 'TRIALING' && sub.trialEndsAt)
    return <>{t('billing.trialEnds', { date: formatDate(sub.trialEndsAt) })}</>;
  if (sub.status === 'PAST_DUE' && sub.pastDueSince)
    return <>{t('billing.pastDueSince', { date: formatDate(sub.pastDueSince) })}</>;
  if (sub.currentPeriodEnd) {
    const key = sub.cancelAtPeriodEnd || sub.complimentary ? 'billing.endsAt' : 'billing.renews';
    return <>{t(key, { date: formatDate(sub.currentPeriodEnd) })}</>;
  }
  if (sub.complimentary) return <>{t('billing.noEnd')}</>;
  if (sub.trialEndsAt) return <>{t('billing.trialEnded', { date: formatDate(sub.trialEndsAt) })}</>;
  return <>—</>;
}

export function CouponValue({ sub }: { sub: BusinessSubscription }) {
  const { t } = useTranslation('admin');
  if (!sub.coupon) return <>—</>;
  return (
    <>{t('billing.couponValue', { percent: sub.coupon.percent, count: sub.coupon.monthsLeft })}</>
  );
}
